/**
 * LegendChips Component
 *
 * Perplexity-style legend chips below the chart.
 * Shows institution name, current rating, and score change.
 * Hover to highlight that series, click X to remove comparison.
 */

import { X } from 'lucide-react';
import type { LegendChip } from './types';
import styles from './TimelineChart.module.css';

interface LegendChipsProps {
  /** Array of chip data for all series */
  chips: LegendChip[];
  /** ID of currently highlighted series (from hover) */
  highlightedId: string | null;
  /** Callback when hovering a chip */
  onHover: (id: string | null) => void;
  /** Callback when removing a comparison */
  onRemove: (id: string) => void;
}

export default function LegendChips({
  chips,
  highlightedId,
  onHover,
  onRemove,
}: LegendChipsProps) {
  // Always show chips when there are series (even single for consistency with hover)
  if (chips.length === 0) {
    return null;
  }

  // Check if any chip has hover data (crosshair active)
  const hasHoverData = chips.some((c) => c.hoverScore !== undefined);

  return (
    <div className={styles.legendContainer}>
      {chips.map((chip) => {
        // When crosshair is active, show hover values instead of defaults
        const displayRating = chip.hoverRating ?? chip.rating;
        const displayScore = chip.hoverScore;

        return (
          <div
            key={chip.institutionId}
            className={`${styles.legendChip} ${
              highlightedId === chip.institutionId ? styles.highlighted : ''
            } ${highlightedId && highlightedId !== chip.institutionId ? styles.dimmed : ''}`}
            onMouseEnter={() => onHover(chip.institutionId)}
            onMouseLeave={() => onHover(null)}
          >
            <div className={styles.chipDot} style={{ backgroundColor: chip.color }} />
            <div className={styles.chipContent}>
              <span className={styles.chipName}>{chip.name}</span>
              <div className={styles.chipStats}>
                <span className={styles.chipRating}>{displayRating}</span>
                {hasHoverData && displayScore !== undefined ? (
                  // Show score when crosshair is active
                  <span className={styles.chipScore}>{displayScore.toFixed(1)}</span>
                ) : (
                  // Show change when not hovering
                  chip.ratingChange && (
                    <span
                      className={`${styles.chipChange} ${
                        chip.ratingChange.startsWith('+')
                          ? styles.positive
                          : chip.ratingChange.startsWith('-')
                          ? styles.negative
                          : ''
                      }`}
                    >
                      {chip.ratingChange} pts
                    </span>
                  )
                )}
              </div>
            </div>
            {!chip.isPrimary && (
              <button
                type="button"
                className={styles.chipRemove}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(chip.institutionId);
                }}
                aria-label={`Remove ${chip.name} from comparison`}
              >
                <X size={12} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
